import { useState } from "react";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Shown when the newest fetched_at for the current scope is older than 24h (or nothing fetched yet).
export default function StaleBanner({ lastUpdated, routeIds, onCompleted, testId = "stale-banner" }) {
  const { api, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const canTrigger = user?.role === "admin" || user?.role === "analyst";
  const ageH = lastUpdated ? Math.floor((Date.now() - new Date(lastUpdated)) / 3600000) : null;

  const refresh = async () => {
    if (!routeIds?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/fares/manual-refresh", { route_ids: routeIds });
      toast.success(`Refresh started for ${data.route_count} route${data.route_count !== 1 ? "s" : ""} — data updates as each route completes`);
      const poll = async () => {
        try {
          const r = await api.get(`/fares/manual-refresh/${data.job_id}`);
          if (r.data.status === "running") setTimeout(poll, 5000);
          else { setBusy(false); onCompleted?.(r.data); }
        } catch { setTimeout(poll, 8000); }
      };
      poll();
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Refresh failed to start");
    }
  };

  return (
    <div data-testid={testId} className="bg-warning/10 border border-warning/30 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-3">
        <Warning size={20} weight="fill" className="text-warning shrink-0" />
        <span className="text-white">
          <strong>Stale data:</strong>{" "}
          {ageH == null ? "no fares have been fetched for this scope yet." : `newest fare was fetched ${ageH}h ago (threshold 24h).`}
        </span>
      </div>
      <button onClick={refresh} disabled={busy || !canTrigger} data-testid={`${testId}-refresh-btn`}
        title={canTrigger ? "Run the full fetch pipeline now" : "Requires Analyst or Admin role"}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning/20 text-warning border border-warning/40 hover:bg-warning/30 disabled:opacity-50 transition-colors whitespace-nowrap">
        <ArrowClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
        {busy ? "Refreshing…" : "Refresh now"}
      </button>
    </div>
  );
}
